import {useContext, useEffect, useRef, useState} from 'react';
import {useSelector} from 'react-redux';
import classNames from 'classnames';
import VectorImage from '../../components/common/VectorImage';
import Image from '../../components/common/Image';
import utils from '../../helpers/utils';
import {IntlContext} from '../../containers/IntlProvider';
import {isEmpty} from 'lodash';
import {isPoweredByNorton} from '../../helpers/partnerUtils';
import {RootState} from '../../reducers/rootReducer';

type DropdownProps = {
  setSelected?: $TSFixMeFunction; // Function that runs when an option is selected
  list: $TSFixMe[]; // list of options for the dropdown
  selectedName?: $TSFixMe; // selected value of the dropdown
  menuMobile?: boolean; // determines whether the dropdown is mobile menu
  isMenu?: boolean; // determines whether the dropdown is a menu or not
  label?: string; // label value for the dropdown
  helperText?: string; // helper text value to be shown below the dropdown
  disabled?: boolean; // determines whether dropdown disabled or enabled
  dataTestId?: $TSFixMe; // test classes for the dropdown and dropdown options
  headerTitle?: string;
  whiteLabelClient?: string; // Identifies white label client associated with the current user. Used to select partner-specific image.
  size?: string;
  newFilterUi?: boolean;
  customCss?: string; // custom styling for the dropdown
  ariaLabel?: string; // aria-label property value
  dropdownCss?: string;
  isAvast3Client?: boolean; // determines whether the client is Avast3
};

/**
 * Renders Dropdown component
 * @param {DropdownProps}
 * @returns {JSX.Element}
 */
const Dropdown = ({
  setSelected,
  list = [],
  isMenu = false,
  selectedName = {},
  menuMobile = false,
  label,
  helperText = '',
  disabled = false,
  dataTestId = {},
  headerTitle,
  whiteLabelClient = '',
  size = 'medium',
  newFilterUi = false,
  customCss = '',
  ariaLabel,
  dropdownCss,
  isAvast3Client = false,
}: DropdownProps) => {
  const [prevItem, setPrevItem] = useState<{key: string; index: number}>({
    key: '',
    index: 0,
  });
  const [listVisible, setListVisible] = useState(false);
  const [focus, setFocus] = useState(false);
  const [hover, setHover] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const localizedStringBundle: StringMap = useContext(IntlContext);
  const auth = useSelector((store: RootState) => store.auth);
  const reimagineThemeEligible = utils.isReimagineThemeEligible(auth);
  const tmpSelectedName = menuMobile ? localizedStringBundle.MENU : utils.safeGet(selectedName, 'name', '');

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutsideOrEsc);
    document.addEventListener('keydown', handleClickOutsideOrEsc);

    return () => {
      document.removeEventListener('mousedown', handleClickOutsideOrEsc);
      document.removeEventListener('keydown', handleClickOutsideOrEsc);
    };
  }, []);

  const preventDefault = (e: Event) => {
    e.preventDefault();
  };

  // modern Chrome requires { passive: false } when adding event
  let supportsPassive = false;
  // @ts-expect-error WindowEventMap types can't be created, and this listener just defines the passive property in the window
  window.addEventListener(
    'test',
    null,
    Object.defineProperty({}, 'passive', {
      get: function() {
        supportsPassive = true;
      },
    })
  );

  const wheelOpt = supportsPassive ? {passive: false} : false;
  const wheelEvent = 'onwheel' in document.createElement('div') ? 'wheel' : 'mousewheel';

  const enableScroll = () => {
    window.removeEventListener('DOMMouseScroll', preventDefault, false);
    if (wheelEvent === 'wheel' || wheelEvent === 'mousewheel') {
      window.removeEventListener(wheelEvent, preventDefault, wheelOpt as EventListenerOptions);
    }
    window.removeEventListener('touchmove', preventDefault, wheelOpt as EventListenerOptions);
  };

  /**
   * Disables scrolling functionality for mouse wheels and touch events
   */
  const disableScroll = () => {
    window.addEventListener('DOMMouseScroll', preventDefault, false); // older FF
    window.addEventListener(wheelEvent, preventDefault, wheelOpt); // modern desktop
    window.addEventListener('touchmove', preventDefault, wheelOpt); // mobile
  };

  useEffect(() => {
    if (listVisible && menuMobile) {
      disableScroll();
    } else {
      enableScroll();
    }

    return enableScroll;
  }, [listVisible]);

  /**
   * toggles dropdown options onClick
   */
  const toggle = () => {
    if (disabled) {
      return;
    }
    setListVisible(!listVisible);
  };

  /**
   * sets state of the variable onClick
   */
  const select = (item: $TSFixMe) => {
    // @ts-expect-error TS(2722) FIXME: Cannot invoke an object which is possibly 'undefin... Remove this comment to see the full error message
    setSelected(item);
  };

  /**
   * Handles onClick event on the dropdown options
   * If item.option is a non-empty object, it will fire a GA track event using the GA options provided.
   * @param {Object} item - dropdown option
   * @param {GaEvent} item.option - GA options for the dropdown item
   */
  const handleOnClick = (item: $TSFixMe) => {
    // @ts-expect-error TS(2722) FIXME: Cannot invoke an object which is possibly 'undefin... Remove this comment to see the full error message
    setSelected(item);
    if (!isEmpty(item.option)) {
      utils.trackEvent(item.option);
    }
    toggle();
  };

  /**
   * Handles onClick event anywhere outside of dropdown
   */

  const handleClickOutsideOrEsc = (e: KeyboardEvent | MouseEvent) => {
    if (e instanceof KeyboardEvent && e.key === 'Escape') {
      setListVisible(false);
      return;
    }

    if (dropdownRef.current && dropdownRef.current.contains(e.target as Node)) {
      return;
    }
    setListVisible(false);
  };

  /**
   * Handles onFocus for dropdown
   */
  const handleOnFocus = () => {
    if (disabled) {
      return;
    }
    setFocus(true);
  };

  /**
   * Handles onBlur for dropdown
   */
  const handleOnBlur = () => {
    setFocus(false);
  };

  /**
   * Moves the focus to the next or previous dropdown option based on the direction provided.
   * @param {number} direction - The direction to move the focus. Positive for down, negative for up.
   */
  const moveFocus = (direction: number) => {
    const items = dropdownRef.current?.querySelectorAll('li');
    if (items) {
      const currentIndex = Array.from(items).indexOf(document.activeElement as HTMLLIElement);
      const nextIndex = (currentIndex + direction + items.length) % items.length;
      (items[nextIndex] as HTMLElement).focus();
    }
  };

  /**
   * Handles onKeyPress event when dropdown list is visible (selects the option and move it to the top of dropdown options based on key pressed)
   * @param {KeyboardEvent} e - event value when a key is pressed on the keyboard
   */
  const handleKeyPress = (e: React.KeyboardEvent<HTMLDivElement>) => {
    let potentialIndex = 0;
    const key = e.key.toUpperCase();
    const dropdown = e.target as HTMLElement;

    switch (key) {
      case 'ENTER':
        if (listVisible) {
          const focusedElement = document.activeElement;
          if (focusedElement && focusedElement.tagName === 'LI') {
            const index = focusedElement.getAttribute('data-id');
            if (index !== null) {
              select(list[parseInt(index)]);
              setListVisible(false);
            }
          }
        } else {
          toggle();
        }
        break;
      case 'ARROWDOWN':
        setListVisible(true);
        moveFocus(1);
        break;
      case 'ARROWUP':
        setListVisible(true);
        moveFocus(-1);
        break;
      case 'ESCAPE':
        setListVisible(false);
        break;
      default:
        break;
    }

    if (prevItem && prevItem.key === key) {
      prevItem.index = prevItem.index + 1;
    } else {
      prevItem.index = 0;
    }

    if (dropdown) {
      let dropdownOptions: NodeListOf<Element> = dropdown.querySelectorAll('.list');
      if (!dropdownOptions || dropdownOptions.length === 0) {
        dropdownOptions = (dropdown as $TSFixMe).parentElement.parentElement.querySelectorAll('.list');
      }

      if (dropdownOptions && dropdownOptions.length > 0) {
        const items = dropdownOptions[dropdownOptions.length - 1].querySelectorAll('li');

        if (items && items.length > 0) {
          let selectedOption;
          const selectedCharArray = [];
          for (let i = 0; i < items.length; i++) {
            if (items[i].innerHTML.indexOf(key) === 0) {
              selectedCharArray.push(items[i]);
            }
          }

          if (selectedCharArray.length > 0) {
            potentialIndex = prevItem.index % selectedCharArray.length;
            selectedOption = selectedCharArray[potentialIndex];

            if (selectedOption) {
              const index = selectedOption.getAttribute('data-id');

              if (index !== null) {
                select(list[parseInt(index)]);
              }
              if (selectedOption.parentElement && selectedOption.parentElement.parentElement) {
                selectedOption.parentElement.parentElement.scrollTop = selectedOption.offsetTop;
              }
            }

            setPrevItem({
              key: key,
              index: potentialIndex,
            });
          }
        }
      }
    }
  };

  /**
   * Render list of dropdown options
   * @returns {JSX.Element[]} - list of dropdown options
   */
  const renderListItems = () => {
    return (
      Array.isArray(list) &&
      list.map((item, index) => {
        const isOnTopEdgeOfMenu = index === 0;
        const isOnBottomEdgeOfMenu = index === list.length - 1;
        const automationSelector = (item.name || '').replace(/[^A-Z0-9]+/gi, '').toLowerCase();
        const rowClass = classNames(
          selectedName.value && selectedName.value === item.value && item.name === selectedName.name
            ? `h-auto px-4 py-4 ${menuMobile ? 'bg-brand' : 'bg-Extra-highlightAccent'}`
            : 'h-auto px-4 py-4',
          (!selectedName.value && item.name === selectedName.name) ||
            (selectedName.value && item.value === selectedName.value)
            ? newFilterUi
              ? 'font-normal'
              : 'font-bold'
            : '',
          menuMobile
            ? 'group reimagine-text-body1 font-medium hover:bg-brand hover:text-onBrand-primary hover:font-semibold'
            : 'bg-background hover:bg-Extra-highlightAccent text-onSurface-primary hover:text-onSurface-primary',
          size === 'small' ? 'flex items-center' : 'block',
          {
            'bg-brand': menuMobile && !selectedName.value && item.name === selectedName.name,
            'bg-Extra-highlightAccent': !menuMobile && !selectedName.value && item.name === selectedName.name,
            'flex items-center': item.logo,
            'hover:bg-accent': item.logo && !menuMobile,
            'rounded-t': menuMobile && isOnTopEdgeOfMenu,
            'rounded-b': menuMobile && isOnBottomEdgeOfMenu,
          }
        );
        const iconClasses = classNames('w-6', {
          'h-6 mr-2': menuMobile,
          'pt-1 mr-4': !menuMobile,
        });
        const logo = !reimagineThemeEligible && item.logo && (
          <span className="relative inline-flex shrink-0 overflow-visible">
            <Image url={item.logo} classes={iconClasses} key={item.logo} />
            {item.showIconBadge && (
              <span
                role="presentation"
                className="absolute -right-0.5 -top-0.5 z-10 h-3 min-h-[12px] w-3 min-w-[12px] rounded-full border-2 border-background"
                style={{backgroundColor: 'var(--accent)'}}
                data-testid="discover-icon-badge"
              />
            )}
          </span>
        );
        const name = item.name && (
          <span
            className={classNames(
              'my-auto mr-4',
              reimagineThemeEligible &&
                selectedName.value &&
                selectedName.value === item.value &&
                item.name === selectedName.name
                ? 'text-onBrand-primary'
                : 'text-onBackground-primary',
              menuMobile && 'group-hover:text-onBrand-primary'
            )}
          >
            {item.name}
          </span>
        );
        const supportContent = item.supportComponent && (
          <span
            className={classNames(
              'my-auto ml-auto mr-2',
              selectedName.value && selectedName.value === item.value && item.name === selectedName.name
                ? classNames('text-onBrand-primary', reimagineThemeEligible && '[&_img]:brightness-0 [&_img]:invert')
                : 'text-onBackground-primary',
              menuMobile &&
                classNames(
                  'group-hover:text-onBrand-primary',
                  reimagineThemeEligible && 'group-hover:[&_img]:brightness-0 group-hover:[&_img]:invert'
                )
            )}
          >
            {item.supportComponent}
          </span>
        );

        return (
          <li
            data-id={index}
            onClick={() => handleOnClick(item)}
            key={item.name}
            tabIndex={-1}
            className={`test-${automationSelector} ${rowClass}`}
            data-testid={item.iconName}
          >
            {logo}
            {logo || (reimagineThemeEligible && menuMobile) ? name : item.name}
            {supportContent}
          </li>
        );
      })
    );
  };

  /**
   * Renders header with logo.
   * @param {string} titleStr - Title in header
   * @param {string} whiteLabelClient - Identifies the white label client associated with the current user. Used to select partner-specific image.
   * @returns {JSX.Element} returns JSX for the header
   */
  function renderHeader(titleStr: string, whiteLabelClient: string) {
    // Partner images will be hosted in nortonLifelock CDN repo
    const partnerCdnPath = utils.getCdnImagesPath('/logo', whiteLabelClient);
    const nortonLifelockCdnPath = utils.getCdnImagesPath('/dsp-northstar/LifelockLogo', whiteLabelClient);

    return (
      <div className="flex w-full justify-center">
        {isPoweredByNorton(whiteLabelClient) ? (
          <div className="w-56">
            <VectorImage alt="logo" url={partnerCdnPath} dataTestId="logo" />
          </div>
        ) : (
          <>
            <div className="mr-2 h-8 w-8 min-w-8">
              <VectorImage
                alt="logo"
                url={whiteLabelClient ? partnerCdnPath : nortonLifelockCdnPath}
                dataTestId="logo"
              />
            </div>
            <h2 className="mb-2 mt-1 break-all text-xl font-bold tracking-normal text-onSurface-primary">{titleStr}</h2>
          </>
        )}
      </div>
    );
  }

  function renderActiveMonitoring() {
    return (
      <div className="my-2 flex justify-center self-center">
        <div className="mx-2 my-auto">
          <VectorImage
            url={utils.getCdnImagesPath('/dsp-northstar/GreenCheck', whiteLabelClient)}
            classes={'w-4 h-4'}
            alt={localizedStringBundle.DARK_WEB_ACTIVE_MONITORING}
          />
        </div>
        <span className="ml-1 mt-0.5 align-middle text-xs font-extrabold text-success">
          {localizedStringBundle.DARK_WEB_ACTIVE_MONITORING}
        </span>
      </div>
    );
  }

  const dropdownContainerClasses = classNames(
    'relative md:max-w-xs',
    disabled ? 'bg-onBackground-extraLight' : newFilterUi && selectedName.value !== '' ? 'bg-accent' : 'bg-surface',
    menuMobile ? 'w-fit my-auto z-60' : 'rounded-md mb-2 md:mb-0 flex-grow',
    size === 'small' ? 'w-44 mb-0' : ''
  );
  const arrowIconClasses = classNames(
    'material-icons text-2xl right-0 inset-y-0',
    menuMobile ? 'ml-1' : 'mr-2 absolute',
    customCss,
    disabled
      ? 'text-onSurface-disabled'
      : newFilterUi && selectedName.value !== ''
      ? 'text-surface'
      : listVisible || focus || hover
      ? menuMobile
        ? 'text-onSurface-primary'
        : 'text-accent'
      : 'text-onSurface-primary'
  );
  const listClasses = classNames(
    'list break-words rounded top-0 left-0 cursor-pointer custom-scrollbar custom-scrollbar-thumb bg-background',
    menuMobile
      ? 'w-auto shadow-lg -mx-30 sm:-mx-20 bg-background mt-10 pl-4 sm:left-20 fixed h-full pr-4 z-60'
      : 'shadow-heavy w-full max-h-72 overflow-y-auto mt-12 absolute z-50',
    !listVisible ? 'hidden' : '',
    reimagineThemeEligible ? 'mt-16' : ''
  );
  const dropdownClasses = classNames(
    'flex items-center w-full break-words pl-4 cursor-pointer',
    newFilterUi && selectedName.value !== '' ? 'text-surface py-1' : 'text-onSurface-primary py-2',
    !menuMobile ? 'rounded-md leading-normal shadow-inner' : '',
    disabled
      ? 'bg-onBackground-extraLight border-onBackground-disabled'
      : (listVisible || focus || hover) && !menuMobile
      ? 'border-accent'
      : menuMobile
      ? 'bg-background'
      : newFilterUi && selectedName.value !== ''
      ? 'bg-accent'
      : 'bg-surface border-onBackground-secondary',
    !listVisible && focus && !tmpSelectedName ? 'border-4' : !menuMobile ? 'border-2' : '',
    size === 'small' ? 'pr-7' : 'pr-6',
    dropdownCss
  );

  const selectedNameClasses = classNames(
    'truncate reimagine-text-body1',
    label ? 'mt-2' : '',
    disabled
      ? 'text-onSurface-secondary'
      : newFilterUi && selectedName.value != ''
      ? 'text-surface'
      : 'text-onSurface-primary',
    isMenu ? 'text-right mb-1 font-bold' : '',
    tmpSelectedName ? 'pt-0.5' : 'mb-6'
  );
  const labelClassNames = classNames(
    'absolute top-0 left-0 font-medium cursor-text pl-4',
    tmpSelectedName || listVisible ? 'pt-1 text-xs mt-1' : 'pt-4 text-base',
    disabled ? 'text-onSurface-secondary' : listVisible || focus ? 'text-accent' : 'text-onSurface-secondary'
  );
  const helperTextClassNames = 'mt-2 text-xs text-onSurface-primary';
  const listTagClasses = classNames(menuMobile ? 'bg-background mb-4' : 'bg-surface list-reset');
  // legacy mobile menu shows the title/logo + active-monitoring header; the Reimagine design omits it.
  const headerContent = !reimagineThemeEligible && menuMobile && (
    <div className="pt-5" data-testid="mobile-menu-header">
      {headerTitle && renderHeader(headerTitle, whiteLabelClient)}
      {renderActiveMonitoring()}
    </div>
  );

  return (
    <div
      className={dropdownContainerClasses}
      onFocus={handleOnFocus}
      onBlur={handleOnBlur}
      onClick={toggle}
      onKeyDown={handleKeyPress}
      tabIndex={0}
      ref={dropdownRef}
      onMouseOver={() => setHover(true)}
      onMouseOut={() => setHover(false)}
      data-testid={dataTestId?.dropdown ? dataTestId.dropdown : ''}
      {...(ariaLabel ? {'aria-label': ariaLabel} : {})} // add aria-label only if prop was passed
    >
      <div className={dropdownClasses}>
        <div className={selectedNameClasses} data-testid={dataTestId?.selectedName ? dataTestId.selectedName : ''}>
          {tmpSelectedName}
        </div>
        {menuMobile || isAvast3Client ? (
          <VectorImage
            url={utils.getCdnImagesPath(
              listVisible ? '/dsp-northstar/expand-less' : '/dsp-northstar/expand-more',
              whiteLabelClient
            )}
            classes="ml-2 w-4 h-6"
            alt={listVisible ? 'expand-less' : 'expand-more'}
          />
        ) : (
          <i className={arrowIconClasses}>{listVisible ? 'arrow_drop_up' : 'arrow_drop_down'}</i>
        )}
      </div>
      <div className={listClasses} data-testid={dataTestId?.list ? dataTestId.list : ''}>
        {headerContent}
        <ul className={listTagClasses} data-testid={menuMobile ? 'mobile-menu-dropdown-list' : 'dropdown-list'}>
          {renderListItems()}
        </ul>
      </div>

      {label && (
        <label className={labelClassNames} data-testid={`label-${label}`}>
          {label}
        </label>
      )}

      {helperText && <div className={helperTextClassNames}>{helperText}</div>}
    </div>
  );
};

export default Dropdown;
